import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import type { LocationTreeNode } from '../pages/WmsLocationManager';
import { locationService, UpdateLocationDto } from '../services/locationService';
import LocationBarcodeView from './LocationBarcodeView';
import { useTranslation } from 'react-i18next';
import { formatLocationDisplayName, getLocationTypeLabel } from '../shared/locationLabels';

interface LocationDetailFormProps {
  node: LocationTreeNode;
  treeData: LocationTreeNode[];
  warehouseId: string;
  onDataChanged: () => void;
  onDeleted: () => void;
  showSnackbar: (msg: string, sev?: 'success' | 'error') => void;
}

const LocationDetailForm: React.FC<LocationDetailFormProps> = ({
  node,
  treeData,
  warehouseId,
  onDataChanged,
  onDeleted,
  showSnackbar,
}) => {
  const { t } = useTranslation('warehouse');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isPickable, setIsPickable] = useState(false);
  const [sortOrder, setSortOrder] = useState('');
  const [aisleId, setAisleId] = useState<string | null>(null);
  const [aisleSide, setAisleSide] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [barcodeData, setBarcodeData] = useState<string | null>(null);

  useEffect(() => {
    setCode(node.Code);
    setName(node.Name);
    setCapacity(node.Capacity != null ? String(node.Capacity) : '');
    setIsActive(node.IsActive);
    setIsPickable(node.IsPickable);
    setSortOrder(String(node.SortOrder ?? 0));
    setAisleId(node.AisleId || null);
    setAisleSide(node.AisleSide || '');
    setBarcodeData(node.BarcodeData || null);
  }, [node]);

  const aisles = flatCollect(treeData, 'Aisle');

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      showSnackbar(t('components.locationDetailForm.codeAndNameRequired'), 'error');
      return;
    }
    try {
      setSaving(true);
      const dto: UpdateLocationDto = {
        Id: node.Id,
        Code: code.trim(),
        Name: name.trim(),
        Type: node.Type,
        AisleId: aisleId || undefined,
        AisleSide: aisleSide || undefined,
        GridX: node.GridX ?? undefined,
        GridY: node.GridY ?? undefined,
        GridWidth: node.GridWidth ?? undefined,
        GridHeight: node.GridHeight ?? undefined,
        Capacity: capacity ? parseFloat(capacity) : undefined,
        IsActive: isActive,
        IsPickable: isPickable,
        SortOrder: sortOrder !== '' && !Number.isNaN(parseInt(sortOrder, 10)) ? parseInt(sortOrder, 10) : undefined,
      };
      await locationService.update(dto);
      showSnackbar(t('components.locationDetailForm.updated'));
      onDataChanged();
    } catch {
      showSnackbar(t('components.locationDetailForm.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await locationService.delete(node.Id);
      showSnackbar(t('components.locationDetailForm.deleted'));
      setDeleteOpen(false);
      onDeleted();
    } catch (err: any) {
      showSnackbar(t('components.locationDetailForm.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateBarcode = async () => {
    try {
      setGenerateLoading(true);
      const res = await locationService.generateBarcode(node.Id);
      const data = res.data?.Data || res.data?.data;
      if (data?.BarcodeData || data?.barcodeData) {
        setBarcodeData(data.BarcodeData || data.barcodeData);
        showSnackbar(t('components.locationDetailForm.barcodeCreated'));
        onDataChanged();
      }
    } catch {
      showSnackbar(t('components.locationDetailForm.barcodeError'), 'error');
    } finally {
      setGenerateLoading(false);
    }
  };

  const childCount = node.Children.length;
  const totalDescendants = countDescendants(node);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip label={getLocationTypeLabel(node.Type, t)} size="small" color="primary" variant="outlined" />
          <Typography variant="h6" fontWeight={600}>{formatLocationDisplayName(node.Name, node.Type, t)}</Typography>
          <Typography variant="caption" color="text.secondary">#{node.SortOrder}</Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label={t('components.locationDetailForm.code')} value={code} onChange={e => setCode(e.target.value)} size="small" fullWidth />
          <TextField label={t('components.locationDetailForm.name')} value={name} onChange={e => setName(e.target.value)} size="small" fullWidth />
          <TextField label={t('components.locationDetailForm.type')} value={getLocationTypeLabel(node.Type, t)} size="small" fullWidth InputProps={{ readOnly: true }} />

          {(node.Type === 'Platform') && (
            <>
              <Autocomplete
                value={aisles.find(a => a.Id === aisleId) || null}
                onChange={(_e, val) => setAisleId(val?.Id || null)}
                options={aisles}
                getOptionLabel={o => `${formatLocationDisplayName(o.Name, o.Type, t)} (${o.Code})`}
                renderInput={(params) => <TextField {...params} label={t('components.locationDetailForm.aisle')} size="small" />}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{t('components.locationDetailForm.side')}</InputLabel>
                <Select value={aisleSide} label={t('components.locationDetailForm.side')} onChange={e => setAisleSide(e.target.value)}>
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="Left">{t('components.locationDetailForm.sideLeft')}</MenuItem>
                  <MenuItem value="Right">{t('components.locationDetailForm.sideRight')}</MenuItem>
                  <MenuItem value="Top">{t('components.locationDetailForm.sideTop', { defaultValue: 'Üst' })}</MenuItem>
                  <MenuItem value="Bottom">{t('components.locationDetailForm.sideBottom', { defaultValue: 'Alt' })}</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          <TextField
            label={t('components.locationDetailForm.capacity')}
            value={capacity}
            onChange={e => setCapacity(e.target.value)}
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('components.locationDetailForm.pickSequence', { defaultValue: 'Toplama Sırası' })}
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value.replace(/[^0-9]/g, ''))}
              size="small"
              type="number"
              inputProps={{ min: 0 }}
            />
            <TextField label="Grid X" value={node.GridX ?? '-'} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Grid Y" value={node.GridY ?? '-'} size="small" InputProps={{ readOnly: true }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={e => setIsActive(e.target.checked)} size="small" />}
              label={t('components.locationDetailForm.active')}
            />
            <FormControlLabel
              control={<Checkbox checked={isPickable} onChange={e => setIsPickable(e.target.checked)} size="small" />}
              label={t('components.locationDetailForm.pickable')}
            />
          </Box>

          {/* Barcode section */}
          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              label={t('components.locationDetailForm.barcode')}
              value={barcodeData || ''}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ flex: 1, minWidth: 150 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={generateLoading ? <CircularProgress size={16} /> : <QrCodeIcon />}
              onClick={handleGenerateBarcode}
              disabled={generateLoading}
            >
              {t('components.locationDetailForm.generate')}
            </Button>
            {barcodeData && (
              <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => setBarcodeOpen(true)}>
                {t('components.locationDetailForm.print')}
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteOpen(true)}
          >
            {t('pages.warehouseSetup.delete')}
          </Button>
        </Box>
      </Paper>

      {/* Children summary */}
      {childCount > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('components.locationDetailForm.childrenTitle', { count: totalDescendants })}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('components.locationDetailForm.childrenColCode')}</TableCell>
                <TableCell>{t('components.locationDetailForm.childrenColName')}</TableCell>
                <TableCell>{t('components.locationDetailForm.childrenColType')}</TableCell>
                <TableCell align="center">{t('components.locationDetailForm.childrenColChild')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {node.Children.map(child => (
                <TableRow key={child.Id} hover>
                  <TableCell>{child.Code}</TableCell>
                  <TableCell>{formatLocationDisplayName(child.Name, child.Type, t)}</TableCell>
                  <TableCell><Chip label={getLocationTypeLabel(child.Type, t)} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">{child.Children.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('components.locationDetailForm.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('components.locationDetailForm.deleteWarning')}</Alert>
          <Typography>
            {t('components.locationDetailForm.deleteQuestion', { name: formatLocationDisplayName(node.Name, node.Type, t), type: getLocationTypeLabel(node.Type, t) })}
          </Typography>
          {totalDescendants > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('components.locationDetailForm.deleteDescendants', { count: totalDescendants })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} /> : <DeleteIcon />}
          >
            {deleting ? t('components.locationDetailForm.deleting') : t('pages.warehouseSetup.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barcode print view */}
      {barcodeOpen && barcodeData && (
        <LocationBarcodeView
          open={barcodeOpen}
          onClose={() => setBarcodeOpen(false)}
          code={node.Code}
          name={node.Name}
          barcodeData={barcodeData}
        />
      )}
    </Box>
  );
};

export default LocationDetailForm;

function flatCollect(nodes: LocationTreeNode[], type: string): LocationTreeNode[] {
  const result: LocationTreeNode[] = [];
  for (const n of nodes) {
    if (n.Type === type) result.push(n);
    result.push(...flatCollect(n.Children, type));
  }
  return result;
}

function countDescendants(node: LocationTreeNode): number {
  let count = node.Children.length;
  for (const child of node.Children) {
    count += countDescendants(child);
  }
  return count;
}
